class ProductGroup extends HTMLElement {
  constructor() {
    super();
    this.PRODUCTS_LIMIT = 250;
    this.TEXT_SELECTOR = '[data-atb-text]';
    this.SUBMIT_SELECTOR = '[type="submit"][name="add"]';
    this.DISABLE_TEXT = window.variantStrings.unavailable || 'Unavailable';
    this.SELECT_OPTION_TEXT = window.variantStrings.productSelectOption || 'Select Option';
    this.PROCESSING_CLASS = 'product-group--processing';
    this.atbSelectors = this.dataset.atbSelectors ? this.dataset.atbSelectors.split(',') : [];
    this.baseURL = this.dataset.baseUrl;
    this.product = this.parseJSON(this.dataset.product || '{}');
    this.productFilters = this.product.metafields.filter || [];
    this.productFiltersKeys = Object.keys(this.productFilters);
    this.additionalFilters = this.product.additional_filters || {};
    this.additionalFiltersOrder = this.product.additional_filters_order || [];
    this.productSubFilters = this.product.sub_filters || [];
    this.collection = this.parseJSON(this.dataset.collection || '{}');
    this.collectionFilters = {};
    this.filterConfig = this.parseJSON(this.dataset.filterConfig || '{}');
    this.filters = {};
    this.subfilters = {};
    this.subfiltersKeys = [];
    this.collectionSubFilters = {};
    this.collectionSubFiltersKeys = [];
    this.filtersSet = false;
    this.products = {};
    this.matchedValues = {};
    this.filterElems = [];
    this.selectedFilters = new Map();

    this.setupEventListeners();
    this.getProducts(this.collection, this.product).then(this.initializeProductGroup.bind(this));
  }

  setupEventListeners() {
    document.addEventListener('productGroup:change', this.onProductGroupChange.bind(this));
    this.addEventListener('change', this.onRadioChange.bind(this));
    this.addEventListener('click', this.onCrossoutClick.bind(this));
  }

  setSelectedFilter(filter, value) {
    if (this.selectedFilters.has(filter) && this.isValidValue(value)) {
      this.selectedFilters.set(filter, value);
    }
  }

  onCrossoutClick(e) {
    // Cross out elements are blocking the radio button. Simulate native radio
    // input change.
    const target = e ? e.target : null;

    if (target && target.classList.contains('filters__option-label--crossout')) {
      const parent = target.parentNode;
      if (parent) {
        const input = parent.querySelector('[type="radio"]');
        if (input) {
          input.click();
        }
      }
    }
  }

  isMainTriggerValue(filter, filterValue, subfilter) {
    const main = this.filterConfig[filter];
    const sub = main ? (main.subfilters || {})[subfilter] : null;
    const triggers = sub ? (sub.triggerValues || {}) : {};
    return triggers[filterValue] ? true : false;
  }

  findProductMatch() {
    let matched = {
      products: {},
      productKeys: []
    };
    let filtered = {};

    for (let i = 0; i < this.additionalFiltersOrder.length; i++) {
      let filter = this.additionalFiltersOrder[i];
      let value = this.selectedFilters.get(filter);

      filtered = (this.matchedValues[filter] || {})[value] || {};

      if (this.isValidValue(value)) {
        // If the 1st loop or no matched values found yet, set matched to filtered.
        // Should only fall into this condition if a selectedFilter value is null
        // meaning it was not yet set.
        if (i === 0 || !matched.productsKeys?.length) {
          matched = filtered;
        }

        if (filtered.productsKeys && filtered.productsKeys.length) {
          if (matched.productsKeys && matched.productsKeys.length) {
            let tempMatched = { products: {}, productsKeys: [] };
            tempMatched.productsKeys = matched.productsKeys.filter((productId) => {
              if (filtered.products[productId]) {
                tempMatched.products[productId] = productId;
              }
              return filtered.products[productId];
            });

            matched = tempMatched;

            if (this.isMainFilter(filter)) {
              tempMatched = { products: {}, productsKeys: [] };
              const subfilterKeys = this.getMainSubfilterKeys(filter);

              for (let i = 0; i < subfilterKeys.length; i++) {
                const key = subfilterKeys[i];
                if (this.isMainTriggerValue(filter, value, key)) {
                  const subfilterValue = this.selectedFilters.get(key);
                  const subfiltered = (this.matchedValues[key] || {})[subfilterValue] || {};

                  if (subfiltered && subfiltered.products) {
                    tempMatched.productsKeys = matched.productsKeys.filter((productId) => {
                      if (subfiltered.products[productId]) {
                        tempMatched.products[productId] = productId;
                      }
                      return subfiltered.products[productId];
                    });

                    matched = tempMatched;
                  }

                }
              }
            }
          } else {
            break;
          }
        } else {
          matched = filtered;
          break;
        }
      }
    }

    return matched;
  }

  onRadioChange(e) {
    this.classList.add(this.PROCESSING_CLASS);

    const target = e ? e.target : null;
    let productUrl = '';
    let matchedProduct = {};

    if (target) {
      const filter = target.name;
      const value = target.value;

      this.setSelectedFilter(filter, value);
    }

    this.updateFilters();

    let matched = this.findProductMatch();

    // Check if any filter does not have a value selected
    const selectedNull = [...this.selectedFilters?.values?.() || []].some(value => value === null);

    // If 1 or more products that match the selected filter values
    if (matched.productsKeys && matched.productsKeys.length) {

      // If all the filters have been selected and there are 2 or more matches OR
      // there's only 1 match found, set the first product as the matched product
      if (!selectedNull || matched.productsKeys.length === 1) {
        matchedProduct = this.products[matched.productsKeys[0]] || {};
      }

      // Get matched product url and redirect to PDP if not current
      productUrl = matchedProduct.url;
    }

    if (productUrl && productUrl !== this.product.url) {
      window.location.href = productUrl;
    } else {
      const context = this;
      const productGroupChange = new CustomEvent('productGroup:change', {
        detail:  {
          component: context,
          matchedProduct: matchedProduct,
          matched: matched
        }
      });
      document.dispatchEvent(productGroupChange);
    }
  }

  onProductGroupChange(e) {
    this.updateAtbElems(e);
    this.classList.remove(this.PROCESSING_CLASS);
  }

  updateFilters() {
    if (this.filterElems.length) {
      const CROSSOUT_CLASS = 'filters__option-label--crossout';
      const DISABLE_CLASS = 'filters__option-label--disable';
      const INPUT_SELECTOR = '[type="radio"]';
      const LABEL_SELECTOR = '.filters__option-label';
      const PARENT_SELECTOR = 'data-group';
      const INACTIVE_GROUP_CLASS = 'filters__group--inactive';
      this.filterElems.forEach((elem) => {
        if (elem) {
          const name = elem.dataset.filterName;
          const value = elem.dataset.filterVal;
          const input = elem.querySelector(INPUT_SELECTOR);
          const label = elem.querySelector(LABEL_SELECTOR);
          
          if (input && label) {
            const parent = elem.closest(`[${PARENT_SELECTOR}="${name}"]`);
            const isMainFilter = parent ? parent.dataset.mainFilter === name : false;

            const {
              disable,
              oos,
              isMatch
            } = this.validFilterCheck(name, value);
      
            if (oos || disable) {
              label.classList.add(CROSSOUT_CLASS);
            } else {
              label.classList.remove(CROSSOUT_CLASS);
            }

            if (disable) {
              input.setAttribute('disabled', '');
              label.classList.add(DISABLE_CLASS);
            } else {
              input.removeAttribute('disabled');
              label.classList.remove(DISABLE_CLASS);
            }

            if (isMatch) {
              input.checked = true;
            } else {
              input.checked = false;
            }

            if (isMainFilter && this.selectedFilters.get(name) === value) {
              const subfilterElems = this.querySelectorAll(`[data-parent-filter="${name}"]`);

              if (subfilterElems.length) {
                subfilterElems.forEach((subfilterElem) => {
                  const subfilterKey = subfilterElem.dataset.group;
                  const isParentTrigger = this.isMainTriggerValue(name, value, subfilterKey);
                  let method = isParentTrigger ? 'remove' : 'add';
                  subfilterElem.classList[method](INACTIVE_GROUP_CLASS);
                });
              }
            }
          }
        }
      });
    }
  }

  updateAtbElems(e) {
    const { detail } = (e || {});
    const { matchedProduct, matched } = (detail || {});


    // SELECT_OPTION_TEXT
    if (this.atbSelectors.length) {
      this.atbSelectors.forEach((selector) => {
        const elems = document.querySelectorAll(`${selector} ${this.SUBMIT_SELECTOR}`);
        if (elems.length) {
          elems.forEach((elem) => {
            if (elem) {
              const atbTextElem = elem.querySelector(this.TEXT_SELECTOR);
              let disabled = true;
              let atbText = this.DISABLE_TEXT;

              if (matched?.productsKeys?.length > 1) {
                atbText = this.SELECT_OPTION_TEXT;
              } else {
                if (matchedProduct && matchedProduct.id) {
                  disabled = !matchedProduct.available;
                  atbText = elem.title || window.variantStrings.addToCart;
                }
              }

              if (disabled) {
                elem.setAttribute('disabled', 'true');
              } else {
                elem.removeAttribute('disabled');
              }

              if (atbTextElem) {
                atbTextElem.innerHTML = atbText;
              }
            }
          });
        }
      });
    }
  }

  validFilterCheck(name, value) {
    let selectedMatch = this.selectedFilters.get(name);
    let disable = false;
    let oos = false;
    let isMatch = value === selectedMatch ? true : false;
    let product = {};
    let matched; // Used to store filtered down products as looping through filters.
    let filtered = {};

    // Check if should disable filter. If no products match the current filter
    // value, no products available.
    // Disabled should not be clickable
    let currentMatches = (this.matchedValues[name] || {})[value] || {};
    if (!(currentMatches.productsKeys && currentMatches.productsKeys.length)) {
      disable = true;
    }

    const len = this.additionalFiltersOrder.length;

    // If only 1 filter, don't filter against the currently selected matches.
    // Doing so will disable the filter because products don't have more than
    // one value for the same filter.
    if (!disable) {
      if (len < 2) {
        product = currentMatches?.productsKeys?.find((productId) => {
          return this.products?.[productId]?.available;
        });
      } else if (this.isValidValue(selectedMatch)) {
  
        for (let i = 0; i < len; i++) {
          let filter = this.additionalFiltersOrder[i];
          let value = this.selectedFilters.get(filter);
  
          // Get product matches for the filter.
          filtered = (this.matchedValues[filter] || {})[value] || {};
  
          // If more than 1 filter, check against the other filters. 
          if (filter !== name && this.isValidValue(value)) {
  
            // Set matched to the currently selected value's matches if matched has
            // not been set.
            if (!matched) {
              matched = filtered;
            }
            
            // If the filter has product matches
            if (filtered.productsKeys && filtered.productsKeys.length) {
              // If there are filtered down products, filter out products in matched
              // that are not in filtered. This tries to find products that are a match
              // to the filter and the other currently selected filter values.
              // If no matches, there are no products with this combination of filter
              // values.
              if (matched.productsKeys && matched.productsKeys.length) {
                let tempMatched = { products: {}, productsKeys: [] };
                tempMatched.productsKeys = matched.productsKeys.filter((productId) => {
                  if (filtered.products[productId]) {
                    tempMatched.products[productId] = productId;
                  }
                  return filtered.products[productId];
                });
    
                // update filtered down to newly filtered down products.
                matched = tempMatched;
              } else {
                break;
              }
            } else {
              matched = filtered;
              break;
            }
          
          // Filter value hasn't been selected
          } else if (value === null) {
            if (!matched) {
              matched = {...currentMatches};
            }
          }
        }
  
        // If there is atleast 1 product left after filtering down, this means there
        // is at least 1 product with this combination of filter values.
        if (matched && matched.productsKeys && matched.productsKeys.length) {
          let valid = matched.productsKeys.find((productId) => {
            return currentMatches.products[productId] === productId;
          });
          product = this.products[valid];
        }
  
        if (!product || !product.available) {
          oos = true;
        }
      }
    }

    return {
      disable,
      oos,
      isMatch,
      product: (product || {})
    }
  }

  parseJSON(str) {
    let obj = str || {};

    if (str) {
      try {
        obj = JSON.parse(obj);
      } catch (err) {
        console.log("****** | file: rs-product-group.js | ProductGroup | parseJSON | err", err)
      }
    }

    return obj;
  }

  initializeProductGroup(products) {
    this.products = products || {};
    this.processFilters();
    this.initializeSelectedFilters();
    this.initializedMatchedValues();
    this.renderFilters();
    this.classList.add('product-group--initialized');
    const productGroupInitialized = new CustomEvent('productGroup:initialized');
    document.dispatchEvent(productGroupInitialized);
  }

  initializeSelectedFilters() {
    let selected = new Map();
    this.additionalFiltersOrder.forEach((filter) => {
      selected.set(filter, null);
    });
    selected = this.productFiltersKeys.reduce((selectedFilters, key) => {
      const isMainFilter = this.isMainFilter(key);
      const value = this.productFilters[key];

      // Only set the filter value from the current product if it's available
      // and the value is valid.
      if (this.product?.available && this.isValidValue(value)) {
        selectedFilters.set(key, value);
      }

      // Initialize main filter subfilters and set to null. 
      if (isMainFilter) {
        const subfilters = this.getMainSubfilterKeys(key);
        if (subfilters.length) {
          subfilters.forEach((subfilter) => {
            const subfilterValues = this.getMainSubfilterValues(key, subfilter) || {};
            if (subfilterValues.valuesKeys.length) {
              selected.set(subfilter, null);
            }
          });
        }
      }
      return selectedFilters;
    }, selected);


    // Only set the initial subfilter value to the current product if it's available.
    if (this.product?.available) {
      selected = this.productSubFilters.reduce((selectedFilters, subfilter) => {
        const {
          filter,
          value
        } = subfilter;
  
        if (selectedFilters.has(filter) && this.isValidValue(value)) {
          selectedFilters.set(filter, value);
        }
        return selectedFilters;
      }, selected);
    }
    this.selectedFilters = selected;
  }

  matchedValuesCheck({ product, matchedValues, filter, value }) {
    if (this.isValidValue(filter) && this.isValidValue(value)) {
      if (!matchedValues[filter]) {
        matchedValues[filter] = {};
      }
      if (!matchedValues[filter][value]) {
        matchedValues[filter][value] = { products: {}, productsKeys: [] };
      }

      // If first time encountering product id, push to product keys
      if (!matchedValues[filter][value].products[product.id]) {
        matchedValues[filter][value].productsKeys.push(product.id);
      }
      matchedValues[filter][value].products[product.id] = product.id;
    }
    return matchedValues;
  }

  initializedMatchedValues() {
    let matchedValues = {};
    this.product.sub_filters?.forEach((subfilter) => {
      const {
        filter,
        value
      } = subfilter;
      matchedValues = this.matchedValuesCheck({ product: this.product, matchedValues, filter, value });
    });
    Object.values(this.products).forEach(product => {
      const filters = (product.metafields || {}).filter || {};
      const filterEntries = Object.entries(filters);
      filterEntries.forEach((entry) => {
        const [filter, value] = entry;
        matchedValues = this.matchedValuesCheck({ product, matchedValues, filter, value });
      });
    });
    
    this.matchedValues = matchedValues;
  }

  async getProducts(collection, product) {
    let products = {};
    let filters = {};
    let subfilters = {};
    let page = 1;
    let count;
    let numRequests;

    // Only include current product if it's available.
    if (product && product.available) {
      products[product.id] = product || {};
    }

    if (collection && collection.handle) {
      const productGroup = product && product.product_group ? product.product_group : '';
      const url = `${this.baseURL}/collections/${collection.handle}/?view=product-group&limit=${this.PRODUCTS_LIMIT}&filter.v.availability=1${productGroup ? '&filter.p.m.filter.ProductGroup=' + productGroup : ''}&page=${page}`;
      
      do {
        try {
          const response = await fetch(url);
          const data = response ? await response.json() : {};
          page++;

          if (data && count === undefined) {
            count = data.products_count || 0;
          }

          if (numRequests === undefined) {
            numRequests = Math.ceil((count || 0) / this.PRODUCTS_LIMIT);
          }

          if (data.products) {
            let productIds = Object.keys(data.products);

            products = productIds.reduce((allProducts, id) => {
              const product = data.products[id];

              if (product && !allProducts[id]) {
                allProducts[id] = product;

                // Add to filters
                const productFilters = product.metafields?.filter || {};
                const productFiltersLen = Object.keys(productFilters);

                if (productFiltersLen) {
                  filters = this.additionalFiltersOrder.reduce((final, filter) => {

                    if (productFilters[filter] !== undefined && productFilters[filter] !== null && productFilters[filter] !== '') {
                      if (!final[filter]) {
                        final[filter] = {
                          label: filter,
                          order: this.filterConfig[filter]?.order || [],
                          values: {}
                        };
                      }
                      
                      final[filter].values[productFilters[filter]] = productFilters[filter];
                    }

                    return final;
                  }, filters);
                }
                if (this.productSubFilters?.length) {
                  subfilters = this.productSubFilters.reduce((final, filter) => {
                    const {
                      filter: filterName,
                      parent,
                      value
                    } = filter;
                    const subfilterConfig = this.filterConfig[parent]?.subfilters?.[filterName];
                    
                    if (subfilterConfig) {
                      if (!final[filterName]) {
                        final[filterName] = {
                          label: filterName,
                          order: this.filterConfig[filterName]?.order || [],
                          values: {}
                        };
                      }
                      if (this.isValidValue(productFilters[filterName])) {
                        final[filterName].values[productFilters[filterName]] = productFilters[filterName];
                      }

                      if (this.isValidValue(value) && !final[filterName].values[value]) {
                        final[filterName].values[value] = value;
                      }
                    }
                    return final;
                  }, subfilters);
                }
              }

              return allProducts;
            }, products);
          }
        } catch (error) {
          console.log("****** | file: rs-product-group.js | ProductGroup | getProducts | error", error)
        }
      } while (count && page <= numRequests)

      this.collectionFilters = filters;
      this.collectionSubFilters = subfilters;
      this.filtersSet = true;
    }
    return products;
  }

  isValidValue(value) {
    return (value || value === '0' || value === 0 || value === false) ? true : false;
  }

  processSubFilters() {
    const subfilters = this.productSubFilters.reduce((displayedFilters, filter) => {
      const {
        filter: filterName,
        parent
      } = filter;
      const parentFilterConfig = this.filterConfig?.[parent];
      const subfilterConfig = parentFilterConfig?.subfilters?.[filterName];

      
      // Check if subfilter configs for product is set up in filter config
      if (subfilterConfig) {
        const values = (this.collectionSubFilters[filterName] || {}).values || {};
        const parentFilter = this.filters[parent];

        if (displayedFilters[filterName] === undefined) {
          displayedFilters[filterName] = { values: {}, parents: {}, valuesKeys: [] };
          this.subfiltersKeys.push(filterName);
        }
        displayedFilters[filterName].values = values;
        displayedFilters[filterName].valuesKeys = Object.keys(values || {});
        displayedFilters[filterName].parents[parent] = parent;
        // Add to main filters
        if (parentFilter) {
          let parentSubfilters = parentFilter.subfilters;
          let parentSubfilter = parentSubfilters?.[filterName];

          if (!parentSubfilters || !parentSubfilter) {
            if (!parentSubfilters) {
              parentSubfilters = { subfilters: {}, subfilterKeys: [] };
            }

            parentSubfilters.subfilters[filterName] = { values, valuesKeys: displayedFilters[filterName].valuesKeys };
            parentSubfilters.subfilterKeys.push(filterName);
          }
          this.filters[parent].subfilters = parentSubfilters;
        }
      }
      return displayedFilters;
    }, {});
    this.subfilters = subfilters;
  }

  processFilters() {
    const filters = this.additionalFiltersOrder.reduce((displayedFilters, filter) => {
      if (filter !== undefined && filter !== null && this.additionalFilters[filter]) {
        if (displayedFilters[filter] === undefined) {
          displayedFilters[filter] = {};
        }

        displayedFilters[filter].values = (this.collectionFilters[filter] || {}).values || {};
      }

      return displayedFilters;
    }, {});

    // Merge in current product filter values if the values were not included in
    // the collection response. This can happen if the current product is not
    // included in the product group collection. Only do this if product is available.
    if (this.product?.available) {
      this.productFiltersKeys.forEach(filter => {
        const value = this.productFilters[filter];
        if (filters[filter] && filters[filter].values && filters[filter].values[value] === undefined && this.isValidValue(value)) {
          filters[filter].values[value] = value;
        }
      });
    }

    this.filters = filters;
    this.processSubFilters();

    // Reconcile additional filters set on current product and actual available products.
    // If a filter has more than 1 value or its subfilters have 2 or more options,
    // leave filter otherwise remove filter.
    const finalAdditionalFilters = {};
    const finalAdditionalFiltersOrder = [];
    this.additionalFiltersOrder?.forEach((filter) => {
      const keys = Object.keys(this.filters[filter]?.values || {});
      const subFiltersKeys = this.filters[filter]?.subfilters?.subfilterKeys?.find((subfilterKey) => {
        return this.filters[filter]?.subfilters?.subfilters?.[subfilterKey]?.valuesKeys?.length || 0;
      });

      if ((keys.length > 1 || subFiltersKeys) && !finalAdditionalFilters[filter]) {
        finalAdditionalFilters[filter] = filter;
        finalAdditionalFiltersOrder.push(filter);
      }
    });

    this.additionalFilters = {...finalAdditionalFilters};
    this.additionalFiltersOrder = [...finalAdditionalFiltersOrder];
  }

  processHeightFilter(value) {
    let height = value || 0;
    let displayNum = '';
    let displayUnit = '';
    let splitHeight = [];

    if (height) {
      if (height >= 12) {
        height = parseFloat(value / 12.0).toFixed(2);
        splitHeight = height.split('.');
        displayUnit = "'";
      } else {
        splitHeight = height.split('.');
        displayUnit = '"';
      }

      if (splitHeight[1] === '0' || splitHeight[1] === '00' || !splitHeight[1]) {
        displayNum = `${splitHeight[0]}`;
      } else {
        displayNum = height;
      }
    }

    return `${displayNum}${displayUnit}`;
  }

  processLightsFilter(value) {
    let label = value;
    if (value === 'T') {
      label = 'Yes';
    } else {
      label = 'No';
    }

    return label;
  }

  getMainFilterSubfilters(filter) {
    return this.filters[filter]?.subfilters;
  }

  getMainSubfilterKeys(filter) {
    return this.getMainFilterSubfilters(filter)?.subfilterKeys || [];
  }

  getMainSubfilterValues(filter, subfilter) {
    const mainSubfilters = this.getMainFilterSubfilters(filter);
    return mainSubfilters?.subfilters?.[subfilter] || {};
  }

  isMainFilter(filter) {
    return this.getMainSubfilterKeys(filter)?.length ? true : false;
  }

  isSubfilter(filter) {
    return !this.filters[filter] && this.subfilters[filter] ? true : false;
  }

  getSubfilterParents(filter) {
    return this.subfilters[filter]?.parents || {};
  }

  isSubfilterParent(filter, subfilter) {
    const parentFilter = this.getSubfilterParents(subfilter)?.[filter];
    return parentFilter && parentFilter === filter;
  }

  renderFilter(name, value, label) {
    let str = '';
    let iconHtml = '';
    const filterConfig = this.filterConfig[name] || {};
    const icon = (filterConfig.icons || {})[value];
    const {
      disable,
      oos,
      isMatch,
      product
    } = this.validFilterCheck(name, value);

    if (name !== undefined && name !== null && name !== '' && this.isValidValue(value)) {
      let isMainFilter = this.isMainFilter(name);

      if (icon) {
        iconHtml = `<img class="filters__option-icon" src="${icon}" role="presentation">`;
      }
      str = `<div
      class="filters__option${oos && product.id ? ' filters__option--oos' : ''}${isMainFilter ? ' filters__option--main' : ''}"
      data-filter-option
      data-filter-name="${name}"
      data-filter-val="${value}">
        <input
          type="radio"
          class="filters__option-radio"
          name="${name}"
          value="${value}"
          ${isMatch ? 'checked' : ''}
          ${disable ? 'disabled' : ''}
          ${isMainFilter ? 'data-main-filter' : ''}
        >
        <label
          class="filters__option-label${oos || disable ? ' filters__option-label--crossout' : ''}${disable ? ' filters__option-label--disable' : ''}"
        >
          <span>${label || value}</span>
          ${iconHtml}
        </label>
      </div>
      `;
    }
    return str;
  }

  transformFilterValue(filter, value) {
    const mapping = {
      "Facet Height": this.processHeightFilter,
      "Lights": this.processLightsFilter
    };
    const mapFunc = mapping[filter];
    let label = this.filterConfig[filter]?.values?.[value]?.label;

    if (mapFunc) {
      label = mapFunc(value);
    }
    return this.isValidValue(label) ? label : value;
  }

  renderFilterGroup({html, filter, value, used}) {
    let str = '';
    let label = '';
    if (!used.has(value)) {
      label = this.transformFilterValue(filter, value);
      str = this.renderFilter(filter, value, label);
    }

    used.set(value, value);

    return {
      html: html += str,
      used
    }
  }

  renderFilters() {
    const filtersHtml = this.additionalFiltersOrder.reduce((finalHtml, filter) => {
      let filtersGroupHtml = '';
      const filterValuesOrder = (this.collectionFilters[filter] || {}).order || [];
      const filterValues = Object.values(this.filters[filter].values || {});
      let used = new Map();
      const filterLabel = this.filterConfig[filter] && this.filterConfig[filter].label ?  this.filterConfig[filter].label : filter;
      const isMainFilter = this.isMainFilter(filter);
      let mainFilterCount = 0;
      let showSubfilters = false;

      let filterValuesHtml = filterValuesOrder.reduce((html, value) => {
        if (this.filters[filter] && this.filters[filter].values && this.isValidValue(this.filters[filter].values[value])) {
          if (!used.has(value)) {
            mainFilterCount += 1;
          }
          let {
            html: updatedHtml,
            used: updatedUsed
          } = this.renderFilterGroup({ html, filter, value, used });
          used = updatedUsed;
          html = updatedHtml;
        } else {
          used.set(value, value);
        }
        return html;
      }, '');

      filterValuesHtml = filterValues.reduce((html, value) => {
        if (this.isValidValue(value)) {
          if (!used.has(value)) {
            mainFilterCount += 1;
          }
          let {
            html: updatedHtml,
            used: updatedUsed
          } = this.renderFilterGroup({ html, filter, value, used });
          used = updatedUsed;
          html = updatedHtml;
        } else {
          used.set(value, value);
        }
        return html;
      }, filterValuesHtml);

      if (filterValuesHtml) {
        filtersGroupHtml = `<div class="filters__group" data-group="${filter}" ${isMainFilter ? 'data-main-filter="' + filter + '"' : ''}>
          <legend class="filters__label">${filterLabel}</legend>
          <div class="filters__options">
            ${filterValuesHtml}
          </div>
        </div>
        `;

        if (isMainFilter) {
          let subUsed = new Map();
          let mainSubfilters = this.filters[filter]?.subfilters;
          let subfiltersGroupHtml = '';
          let subfilterValuesHtml = '';
          mainSubfilters?.subfilterKeys?.forEach((subfilter) => {
            let subFilterCount = 0;
            const values = mainSubfilters.subfilters[subfilter]?.valuesKeys || [];
            const selectedParentValue = this.selectedFilters.get(filter);
            let showFilter = false;
            subfilterValuesHtml = values.reduce((html, value) => {
              if (!showFilter && this.filterConfig[filter]?.subfilters?.[subfilter]?.triggerValues?.[selectedParentValue]) {
                showFilter = true;
              }

              if (!subUsed.has(value)) {
                subFilterCount += 1;
              }
              let {
                html: updatedHtml,
                used: updatedUsed
              } = this.renderFilterGroup({ html, filter: subfilter, value, used: subUsed });
              subUsed = updatedUsed;
              return updatedHtml;
            }, subfilterValuesHtml);

            // Render the subfilter only if it has more than 1 option.
            if (subfilterValuesHtml && subFilterCount > 1) {
              subfiltersGroupHtml += `
              <div
                class="filters__group${showFilter ? '' : ' filters__group--inactive'}"
                data-group="${subfilter}"
                data-sub-filter="${subfilter}"
                data-parent-filter="${filter}"
              >
                <legend class="filters__label">${subfilter}</legend>
                <div class="filters__options">
                  ${subfilterValuesHtml}
                </div>
              </div>
              `;
            }
          });

          if (subfiltersGroupHtml) {
            filtersGroupHtml += subfiltersGroupHtml;
            showSubfilters = true;
          }
        }
      }

      // Don't render main filter if:
      // 1) The filter only has 1 option and it doesn't have any subfilters.
      // 2) The filter has only 1 option and all of its subfilters only have 1 option.
      if (!showSubfilters && mainFilterCount < 2) {
        filtersGroupHtml = '';
      }

      return finalHtml += filtersGroupHtml;
    }, '');

    if (filtersHtml) {
      const wrapper = document.createElement('div');
      wrapper.classList.add('filters__wrapper');
      wrapper.innerHTML = filtersHtml;
      this.prepend(wrapper);
      this.filterElems = this.querySelectorAll('[data-filter-option]');
    }
  }
}

customElements.define('product-group', ProductGroup);